import { findCatalogueModelsContaining, buildTypedModelChoiceMessage } from '@/utils/carfax_make_model_resolution'

/*
 * The prompt for a model the operator TYPED, shared by both make/model selectors.
 *
 * ⚠ WHY IT EXISTS. Typing "812" into the Model field files it as free text, and vue-multiselect makes
 * that the path of least resistance: it unshifts the tag row to index 0 and the pointer starts there,
 * so Enter takes the free-text "812" even though "812 Superfast" is visible directly beneath it. The
 * catalogue already knew the answer and offered it as a silent option. Garrett, 17 Sep: "Anytime we
 * have a match that's not in the list that fits a partial... I think we should reconfirm them. Are you
 * sure it's not one of these?"
 *
 * ⚠ SEPARATE FROM decodedModelChoice, which this sits beside rather than inside. That mixin derives
 * everything from a lookupResult and returns null without one; a typed model has no lookupResult at
 * all. The two prompts share the table component, not the state.
 *
 * ⚠ THE TWO TRIGGERS DIFFER IN WHETHER ANYTHING IS WRITTEN, and the difference is deliberate:
 *   Enter/Tab  vue-multiselect fires @tag, the host commits free text as it always has, then asks.
 *   Click-away deactivate() clears the search box and emits close, committing NOTHING. So the prompt
 *              asks about text the record does not hold, and Cancel must leave it that way.
 *   Dean, 20 Sep: "prompt, but commit nothing" on the close path.
 *
 * WHAT THE HOST MUST PROVIDE (both selectors already do):
 *   data     selectedMakeModel, selectedModel, selectedYear, localModels
 *   computed selectedMake
 *   methods  addToUniqueList(value, list), onSelectedModel(model)
 */
export default {
  data: function () {
    return {
      // The prompt is on screen.
      showTypedModelChoice: false,
      // Whether the prompt has a row ticked. The typed value is pre-checked, so it opens applyable.
      typedModelChoiceApplyable: false,
      // What the operator typed, held while the prompt is up.
      typedModelName: '',
      /*
       * The catalogue rows offered for it.
       *
       * ⚠ DECLARED, not assigned into existence. raiseTypedModelChoice writes this and the table binds
       * to it, and on Vue 2 an undeclared property is not reactive - the prompt would open with an
       * empty list and no error. The RO mixin carries the same warning for the same reason.
       */
      typedModelCandidates: [],
      // Whether the typed value had already been written to the record when the prompt opened.
      typedModelCommitted: false,
      /*
       * ⚠ THE ANTI-LOOP LATCH, and it holds VALUES rather than a boolean. Dean, 20 Sep: once they keep
       * "812" it must never ask about "812" again, but "488" still has to ask.
       *
       * ⚠ It is also shared with the decode prompt's cancel, which is not tidiness. That cancel calls
       * offerModelListWhenNotInCatalogue, which re-opens the model list and SEEDS THE SEARCH BOX with
       * the group name - so closing that list hands the close trigger leftover text and would raise
       * this prompt about the value the operator just dismissed. Recording it here is what stops the
       * two prompts ping-ponging.
       */
      answeredTypedModels: {}
    }
  },
  computed: {
    typedModelChoiceMakeModel: function () {
      if (!this.typedModelName) return null
      //modelId 0 marks it as free text, which is what colours the typed row red beside the catalogue
      //rows - the same shape decodedModelChoiceMakeModel uses.
      return { modelId: 0, customModelName: this.typedModelName, productionYear: this.selectedYear }
    },
    typedModelChoiceMessage: function () {
      return this.typedModelName ? buildTypedModelChoiceMessage(this.typedModelName) : ''
    }
  },
  methods: {
    //⚠ Case and spacing insensitive, so "812 " and "812" are one answer rather than two.
    typedModelAnswerKey: function (text) {
      return String(text === null || text === undefined ? '' : text).replace(/\s+/g, ' ').trim().toUpperCase()
    },
    markTypedModelAnswered: function (text) {
      const answerKey = this.typedModelAnswerKey(text)
      if (!answerKey) return
      //⚠ $set, not a plain assignment: answeredTypedModels is a plain object and Vue 2 cannot see a
      //new key added to it, so the guard below would keep reading stale and re-raise.
      this.$set(this.answeredTypedModels, answerKey, true)
    },
    hasAnsweredTypedModel: function (text) {
      const answerKey = this.typedModelAnswerKey(text)
      return !!answerKey && this.answeredTypedModels[answerKey] === true
    },
    /*
     * The catalogue can no longer answer for what was typed once the make or the year moves, so the
     * latch is dropped with it. onSelectedYear already wipes localModels for the same reason.
     */
    forgetAnsweredTypedModels: function () {
      this.answeredTypedModels = {}
    },
    /*
     * Ask which model the operator meant, if the catalogue can offer anything.
     *
     * @param text      what they typed
     * @param committed whether the host has already written it to the record (Enter/Tab) or not (close)
     * @returns whether the prompt was raised
     */
    raiseTypedModelChoice: function (text, committed) {
      if (this.showTypedModelChoice) return false
      if (this.hasAnsweredTypedModel(text)) return false

      const matches = findCatalogueModelsContaining(this.selectedMake, text, this.selectedYear)
      //⚠ Nothing to offer is NOT an answer - the operator keeps their value and the existing
      //not-in-catalogue warning still has its say. Latching here would be silent and wrong.
      if (matches.length === 0) return false

      this.typedModelName = text
      this.typedModelCommitted = committed === true
      this.typedModelCandidates = matches
      //⚠ The typed value is pre-checked, so Apply without touching the table keeps it. That is the
      //whole reason this can be dismissed two ways without trapping anyone.
      this.typedModelChoiceApplyable = true
      this.showTypedModelChoice = true
      return true
    },
    /*
     * Applying a row. The typed value is itself a row, so this also covers "no, I meant what I said".
     *
     * ⚠ Routed through onSelectedModel, not a bare assignment: nothing watches selectedModel, and
     * onSave reads selectedMakeModel - the defect that made the pop-fill chips save nothing.
     */
    onTypedModelChoiceApply: function (chosenRow) {
      if (!chosenRow) return

      const typed = this.typedModelName
      const chosen = chosenRow.model && chosenRow.model.id > 0
        ? chosenRow.model
        : { id: -1, name: chosenRow.name, productionYear: this.selectedYear, trim: '', style: '' }

      this.addToUniqueList(chosen, this.localModels)
      this.selectedModel = chosen
      this.onSelectedModel(chosen)
      this.markTypedModelAnswered(typed)
      this.closeTypedModelChoice()
    },
    /*
     * Cancel keeps whatever the record already held.
     *
     * ⚠ It deliberately writes NOTHING. On the Enter path the free text is already committed, so
     * keeping it needs no action; on the close path vue-multiselect discarded the text and the record
     * must stay as it was. Writing the typed value here would make click-away commit free text, which
     * is the one thing that path must not do.
     */
    onTypedModelChoiceCancel: function () {
      this.markTypedModelAnswered(this.typedModelName)
      this.closeTypedModelChoice()
    },
    onTypedModelChoiceChanged: function (hasChoice) {
      this.typedModelChoiceApplyable = hasChoice === true
    },
    closeTypedModelChoice: function () {
      this.showTypedModelChoice = false
      this.typedModelName = ''
      this.typedModelCommitted = false
      this.typedModelCandidates = []
    }
  }
}
